/**
 * 类型映射工具函数
 * 集中管理项目中所有数字/布尔类型对应的中文名称
 */

// 定义基础映射结构
type BaseMapping = Record<
  string | number,
  string | { typeLabel: string; unitLabel: string }
>

// 1. 定义各类别的映射关系
const TYPE_MAPPINGS = {
  // 分类类型：0 课时 1 课程
  categoryType: {
    0: '课时',
    1: '课程'
  } as Record<number, string>,
  // 学习类型：1=练习,2=作业,3=闯关,4=任务
  learningType: {
    1: '练习',
    2: '作业',
    3: '闯关',
    4: '任务'
  } as Record<number, string>,
  // 试卷来源：true=题目组合 | false=试卷
  paperSourceType: {
    1: {
      typeLabel: '题目组合',
      unitLabel: '道题'
    },
    0: {
      typeLabel: '试卷',
      unitLabel: '张试卷'
    }
  } as Record<string, { typeLabel: string; unitLabel: string }>
} satisfies Record<string, BaseMapping>

// 提取所有合法 typeKey 类型
export type TypeKey = keyof typeof TYPE_MAPPINGS

/**
 * 获取类型对应的中文名称
 * @param typeKey - 类型键名
 * @param value - 要转换的值
 * @param defaultValue - 匹配不到时的默认值
 * @returns 对应的中文名称
 */
export function getTypeLabel<T extends TypeKey>(
  typeKey: T,
  value: string | number | boolean | undefined | null,
  defaultValue = ''
): string {
  if (value === undefined || value === null) return defaultValue

  const typeMap = TYPE_MAPPINGS[typeKey]
  if (!typeMap) return defaultValue

  // 处理普通字符串映射
  return (
    (typeMap as Record<string | number, string>)[String(value)] ?? defaultValue
  )
}

/**
 * 获取试卷来源对应的单位（道题/张试卷）
 * @param paperSource - 试卷来源布尔值
 * @param defaultValue - 默认单位
 * @returns 单位文本
 */
export function getPaperSourceUnit(
  paperSource: boolean | undefined | null,
  defaultValue = '张试卷'
): string {
  if (paperSource === undefined || paperSource === null) return defaultValue
  return (
    TYPE_MAPPINGS.paperSourceType[String(paperSource)]?.unitLabel ??
    defaultValue
  )
}

/**
 * 获取指定类型的所有映射选项（适用于下拉选择框）
 * @param typeKey - 类型键名
 * @returns 选项列表 { label: string, value: number | boolean }[]
 */
export function getTypeOptions<T extends TypeKey>(
  typeKey: T
): Array<{ label: string; value: number | boolean }> {
  const typeMap = TYPE_MAPPINGS[typeKey]
  if (!typeMap) return []

  return Object.entries(typeMap).map(([key, val]) => ({
    label: typeof val === 'string' ? val : val.typeLabel,
    value: isNaN(Number(key)) ? key === 'true' : Number(key)
  }))
}

// 导出原始映射对象（供特殊场景使用）
export const TYPE_MAP = TYPE_MAPPINGS

export const TypeMappingPlugin = {
  install(app: any) {
    // 注册全局属性
    app.config.globalProperties.$getTypeLabel = getTypeLabel
    app.config.globalProperties.$getPaperSourceUnit = getPaperSourceUnit
    app.config.globalProperties.$getTypeOptions = getTypeOptions
  }
}

// 给 Vue 模板添加类型提示（TS 专属，避免编辑器报错）
declare module 'vue' {
  interface ComponentCustomProperties {
    $getTypeLabel: typeof getTypeLabel
    $getPaperSourceUnit: typeof getPaperSourceUnit
    $getTypeOptions: typeof getTypeOptions
  }
}
